import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2, Bell, Send, AlertCircle, Smartphone, Globe, User as UserIcon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Owner-only tool to fire a test push at any registered user (web + iOS).
 *
 * Purpose: end-to-end verification of the APNs sender against a real device
 * without needing to trigger a real notification event. Shows per-channel
 * delivery breakdown so it's obvious when iOS is failing vs web push.
 */
export default function AdminPushPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [title, setTitle] = useState('Kinsy admin test');
  const [body, setBody] = useState('If you see this, push is working.');
  const [deeplink, setDeeplink] = useState('/app');
  const [sending, setSending] = useState(false);
  const [sendingPlain, setSendingPlain] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);
  const [selfDiag, setSelfDiag] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (!user.is_owner) {
      navigate('/app', { replace: true });
      return;
    }
    (async () => {
      try {
        const [{ data: usersList }, { data: diag }] = await Promise.all([
          axios.get(`${API}/admin/push/users`, { withCredentials: true }),
          axios.get(`${API}/push/diagnostics`, { withCredentials: true }),
        ]);
        setUsers(usersList);
        setSelfDiag(diag);
        if (usersList.length > 0) setSelectedId(usersList[0].id);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load users');
      }
    })();
  }, [user, navigate]);

  /**
   * Interpret the result JSON and surface a toast that reflects the
   * ACTUAL APNs verdict — never say "Sent" unless at least one device
   * came back with `is_successful: true`. Rejections are surfaced with
   * status + reason so it's obvious what Apple said.
   */
  const summarizeAndToast = (data) => {
    const devices = Array.isArray(data?.ios?.devices) ? data.ios.devices : [];
    const iosAccepted = devices.filter((d) => d.is_successful).length;
    const iosRejected = devices.filter((d) => !d.is_successful && (d.status || d.reason || d.exception)).length;
    const webAccepted = data?.web?.sent || 0;

    if (iosAccepted > 0) {
      const first = devices.find((d) => d.is_successful);
      toast.success(
        `iOS accepted by APNs — status ${first?.status || 200} — apns-id ${first?.notification_id || '—'}`,
        { duration: 8000 },
      );
    } else if (iosRejected > 0) {
      const first = devices.find((d) => !d.is_successful) || {};
      toast.error(
        `iOS rejected — status ${first.status || '—'} — reason ${first.reason || first.exception || 'unknown'}`,
        { duration: 12000 },
      );
    } else if (data?.ios?.no_tokens) {
      toast.warning('Target user has no registered iOS devices.');
    } else if (data?.ios?.not_configured) {
      toast.error('Server-side APNs is not configured (missing key/team/bundle in backend/.env).');
    } else if (webAccepted > 0) {
      toast.info(`Web push accepted by ${webAccepted} browser(s). No iOS device attempted.`);
    } else {
      toast.warning('Test push attempted — nothing accepted. See per-device breakdown below.');
    }
  };

  const handleSend = async () => {
    if (!selectedId) {
      toast.error('Pick a user first');
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/push/send`,
        { user_id: selectedId, title, body, deeplink: deeplink || null },
        { withCredentials: true }
      );
      setLastResult(data);
      summarizeAndToast(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  /**
   * Fire the FIXED plain-alert diagnostic. Uses the dedicated
   * /admin/push/ios-test-plain endpoint whose payload is server-side
   * hard-coded to the exact spec — no deeplink, no data extras, no
   * mutable-content, no content-available. Purpose: prove background
   * banner delivery in isolation from any feature payload variance.
   */
  const handleSendPlain = async () => {
    if (!selectedId) {
      toast.error('Pick a user first');
      return;
    }
    setSendingPlain(true);
    setLastResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/push/ios-test-plain?user_id=${encodeURIComponent(selectedId)}`,
        {},
        { withCredentials: true }
      );
      // Wrap in the same result shape the regular send returns so the
      // per-device UI below can render identically.
      const wrapped = {
        target: { id: selectedId, name: users?.find((u) => u.id === selectedId)?.name, email: users?.find((u) => u.id === selectedId)?.email },
        web: { sent: 0 },
        ios: data,
      };
      setLastResult(wrapped);
      summarizeAndToast(wrapped);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Plain-test send failed');
    } finally {
      setSendingPlain(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24 md:pb-8">
      <Header />
      <main className="max-w-3xl mx-auto px-4 py-6" data-testid="admin-push-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2">
            <Bell className="w-6 h-6 text-teal-600" /> Push notification diagnostics
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Diagnostic tool only. Fires a raw APNs / web push request at a chosen user and
            reports Apple&apos;s exact verdict. Not the product notification experience —
            real Kinsy notifications are event-driven and delivered automatically.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex items-center gap-2" data-testid="admin-push-error">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        {users === null && !error && (
          <div className="flex items-center gap-2 text-stone-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
          </div>
        )}

        {users !== null && users.length === 0 && (
          <Card className="p-6 text-center text-stone-600 border-dashed" data-testid="admin-push-empty">
            No users have push subscriptions yet. Install the TestFlight build or enable web push in Profile → Notifications first.
          </Card>
        )}

        {selfDiag && (
          <Card className="p-4 rounded-2xl bg-white border-stone-200 mb-4" data-testid="admin-push-server-diag">
            <div className="text-xs font-semibold text-stone-700 mb-2">Server APNs configuration</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>APNs configured: <span className={selfDiag.apns_configured ? 'text-green-700 font-mono' : 'text-red-700 font-mono'}>{String(selfDiag.apns_configured)}</span></div>
              <div>Topic: <span className="font-mono">{selfDiag.apns_topic || '—'}</span></div>
              <div>Environment: <span className="font-mono">{selfDiag.apns_environment}</span></div>
              <div>Your iOS tokens: <span className="font-mono">{selfDiag.ios_tokens_count}</span></div>
            </div>
          </Card>
        )}

        {users !== null && users.length > 0 && (
          <Card className="p-5 rounded-2xl bg-white border-stone-200 space-y-4">
            <div>
              <Label htmlFor="admin-push-user" className="text-sm text-stone-700">Send to</Label>
              <select
                id="admin-push-user"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                data-testid="admin-push-user-select"
                className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email}) — iOS {u.ios_devices} · Web {u.web_subscriptions}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="admin-push-title" className="text-sm text-stone-700">Title</Label>
              <Input
                id="admin-push-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={80}
                data-testid="admin-push-title-input"
              />
            </div>

            <div>
              <Label htmlFor="admin-push-body" className="text-sm text-stone-700">Body</Label>
              <Textarea
                id="admin-push-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={200}
                rows={3}
                data-testid="admin-push-body-input"
              />
            </div>

            <div>
              <Label htmlFor="admin-push-deeplink" className="text-sm text-stone-700">Deep-link path (optional)</Label>
              <Input
                id="admin-push-deeplink"
                value={deeplink}
                onChange={(e) => setDeeplink(e.target.value)}
                placeholder="/app"
                data-testid="admin-push-deeplink-input"
              />
              <p className="text-xs text-stone-500 mt-1">Tapping the push opens this route inside the app.</p>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                onClick={handleSend}
                disabled={sending || sendingPlain || !selectedId}
                className="w-full rounded-full bg-teal-600 hover:bg-teal-700 text-white"
                data-testid="admin-push-send-btn"
              >
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Attempting…</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Attempt test push</>
                )}
              </Button>
              <Button
                onClick={handleSendPlain}
                disabled={sending || sendingPlain || !selectedId}
                variant="outline"
                className="w-full rounded-full border-stone-300"
                data-testid="admin-push-send-plain-btn"
                title='Sends a fixed diagnostic payload: {"aps":{"alert":{"title":"Kinsy Test","body":"…"},"sound":"default","badge":1},"type":"test"}'
              >
                {sendingPlain ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Attempting…</>
                ) : (
                  <>Attempt fixed plain test</>
                )}
              </Button>
              <p className="text-[11px] text-stone-500 leading-relaxed">
                Diagnostic tool only. Displays exact APNs response.
                A “Sent” outcome requires <span className="font-mono">is_successful: true</span> from Apple —
                nothing here confirms an actual banner appeared on the device.
              </p>
            </div>

            {lastResult && (
              <div className="mt-2 p-3 rounded-lg bg-stone-50 border border-stone-200 text-sm space-y-3" data-testid="admin-push-result">
                <div className="font-medium text-stone-800 flex items-center gap-2">
                  <UserIcon className="w-4 h-4" /> {lastResult.target?.name} ({lastResult.target?.email})
                </div>
                {(() => {
                  const devs = Array.isArray(lastResult.ios?.devices) ? lastResult.ios.devices : [];
                  const iosAccepted = devs.filter((d) => d.is_successful).length;
                  const iosRejected = devs.filter((d) => !d.is_successful && (d.status || d.reason || d.exception)).length;
                  const webAccepted = lastResult.web?.sent || 0;
                  const webRejected = 0;
                  return (
                    <div className="grid grid-cols-2 gap-2 text-xs" data-testid="admin-push-summary">
                      <div className="rounded-lg px-3 py-2 bg-white border border-stone-200">
                        <div className="flex items-center gap-2 font-semibold text-stone-700"><Smartphone className="w-4 h-4" /> iOS APNs</div>
                        <div className="mt-1 text-green-700">accepted: <span className="font-mono">{iosAccepted}</span></div>
                        <div className="text-red-700">rejected: <span className="font-mono">{iosRejected}</span></div>
                        <div className="text-stone-500">targeted: <span className="font-mono">{devs.length}</span></div>
                      </div>
                      <div className="rounded-lg px-3 py-2 bg-white border border-stone-200">
                        <div className="flex items-center gap-2 font-semibold text-stone-700"><Globe className="w-4 h-4" /> Web push</div>
                        <div className="mt-1 text-green-700">accepted: <span className="font-mono">{webAccepted}</span></div>
                        <div className="text-red-700">rejected: <span className="font-mono">{webRejected}</span></div>
                      </div>
                    </div>
                  );
                })()}

                {/* Per-device verdict — the ONLY line here that reflects
                    what Apple actually said. Everything above summarises;
                    this is the ground truth. */}
                {Array.isArray(lastResult.ios?.devices) && lastResult.ios.devices.length > 0 && (
                  <div className="space-y-2" data-testid="admin-push-devices">
                    <div className="text-xs font-semibold text-stone-700">Per-device APNs verdict</div>
                    {lastResult.ios.devices.map((d, i) => (
                      <div key={i} className={`text-xs rounded-lg px-3 py-2 space-y-1 border ${d.is_successful ? 'bg-green-50 border-green-200 text-green-900' : 'bg-red-50 border-red-200 text-red-900'}`}>
                        <div className="flex items-center gap-2">
                          <Smartphone className="w-3.5 h-3.5" />
                          <span className="font-mono">…{d.token_last8}</span>
                          <span className="ml-auto font-semibold">
                            {d.is_successful
                              ? `iOS accepted by APNs — status ${d.status || 200} — apns-id ${d.notification_id || '—'}`
                              : `iOS rejected — status ${d.status || '—'} — reason ${d.reason || d.exception || 'unknown'}`}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px] text-stone-700">
                          <div>endpoint: <span className="font-mono">{d.endpoint || '—'}</span></div>
                          <div>environment: <span className="font-mono">{d.environment || '—'}</span></div>
                          <div>apns-topic: <span className="font-mono">{d.topic || '—'}</span></div>
                          <div>apns-push-type: <span className="font-mono">{d.push_type || '—'}</span></div>
                          <div>apns-priority: <span className="font-mono">{d.priority ?? '—'}</span></div>
                          <div>apns-id: <span className="font-mono break-all">{d.notification_id || '—'}</span></div>
                          <div>aps.alert present: <span className={`font-mono ${d.has_alert ? 'text-green-700' : 'text-red-700'}`}>{String(!!d.has_alert)}</span></div>
                          <div>sound present: <span className={`font-mono ${d.has_sound ? 'text-green-700' : 'text-stone-600'}`}>{String(!!d.has_sound)}</span></div>
                          <div>badge present: <span className={`font-mono ${d.has_badge ? 'text-green-700' : 'text-stone-600'}`}>{String(!!d.has_badge)}</span></div>
                          {d.removed && <div className="col-span-2 text-amber-700">token removed (dead)</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {lastResult.ios?.no_tokens && (
                  <div className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                    Target user has no iOS devices registered. Ask them to open the TestFlight app and tap Allow when the notification prompt appears.
                  </div>
                )}
                {lastResult.ios?.not_configured && (
                  <div className="text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">
                    Server-side APNs is not configured (missing key/team/bundle in backend/.env).
                  </div>
                )}

                {/* Show the raw payload we sent so the recipient can verify
                    aps.alert.title/body and sound. */}
                {lastResult.ios?.payload && (
                  <details className="text-xs" data-testid="admin-push-payload-details">
                    <summary className="cursor-pointer text-stone-600 hover:text-stone-900">Sent payload (JSON)</summary>
                    <pre className="mt-1 bg-white rounded-lg p-2 border border-stone-200 overflow-auto text-[11px] text-stone-800">
{JSON.stringify(lastResult.ios.payload, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}
          </Card>
        )}
      </main>
      <MobileNav />
    </div>
  );
}
